(function () {
  if (customElements.get('floral-hero-carousel')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FloralHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.copies = Array.from(this.querySelectorAll('[data-hero-copy]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.prevBtn = this.querySelector('[data-hero-prev]');
      this.nextBtn = this.querySelector('[data-hero-next]');
      this.playToggle = this.querySelector('[data-hero-toggle]');
      this.indexNow = this.querySelector('[data-hero-index]');
      this.indexTotal = this.querySelector('[data-hero-total]');
      this.count = this.slides.length;
      this.current = 0;
      this.timer = null;
      this.touchStartX = null;
      this.userPaused = false;
      this.interval = Number(this.dataset.interval || 6000);
      this.autoplayEnabled = this.dataset.autoplay === 'true';

      if (!this.count) return;

      this.onPrev = () => this.prev(true);
      this.onNext = () => this.next(true);
      this.onToggle = () => this.togglePlay();
      this.onKey = (event) => this.handleKey(event);
      this.onEnter = () => this.stopAutoplay();
      this.onLeave = () => this.resumeIfAllowed();
      this.onFocusIn = () => this.stopAutoplay();
      this.onFocusOut = () => this.resumeIfAllowed();
      this.onTouchStart = (event) => {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      };
      this.onTouchEnd = (event) => this.handleTouchEnd(event);
      this.onBlockSelect = (event) => this.handleBlockSelect(event);
      this.onBlockDeselect = (event) => this.handleBlockDeselect(event);
      this.onSectionLoad = (event) => this.handleSectionLoad(event);
      this.onReduceChange = () => {
        if (reduceMotion.matches) this.stopAutoplay();
        else this.resumeIfAllowed();
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      if (this.playToggle) this.playToggle.addEventListener('click', this.onToggle);
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i, true));
      });
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      reduceMotion.addEventListener('change', this.onReduceChange);

      this.applySlide(this.current);
      this.preloadSlides();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.playToggle) this.playToggle.removeEventListener('click', this.onToggle);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      reduceMotion.removeEventListener('change', this.onReduceChange);
    }

    isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.count > 1 &&
        !this.userPaused &&
        !reduceMotion.matches &&
        !this.classList.contains('is-paused')
      );
    }

    applySlide(index, prevIndex, direction) {
      const incoming = this.slides[index];
      if (direction && incoming) {
        // Park the incoming slide on the side it should enter from, without animating.
        incoming.classList.add('is-instant');
        incoming.classList.toggle('is-prev', direction < 0);
        void incoming.offsetWidth;
        incoming.classList.remove('is-instant');
      }
      this.slides.forEach((slide, i) => {
        if (i === index) {
          slide.classList.add('is-active');
          slide.classList.remove('is-prev');
        } else if (i === prevIndex) {
          slide.classList.remove('is-active');
          slide.classList.toggle('is-prev', direction > 0);
        } else {
          slide.classList.remove('is-active');
          if (prevIndex === undefined) slide.classList.remove('is-prev');
        }
      });
      this.copies.forEach((copy, i) => {
        const active = i === index;
        copy.classList.toggle('is-active', active);
        copy.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (copy.inert !== undefined) copy.inert = !active;
      });
      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.remove('is-active');
        if (active) {
          void dot.offsetWidth;
          dot.classList.add('is-active');
        }
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (this.indexNow) {
        this.indexNow.textContent = String(index + 1).padStart(2, '0');
      }
      if (this.indexTotal) {
        this.indexTotal.textContent = String(this.count).padStart(2, '0');
      }
    }

    goTo(index, userTriggered, direction) {
      if (!this.count) return;
      const target = (index + this.count) % this.count;
      if (target !== this.current) {
        const prevIndex = this.current;
        this.current = target;
        this.applySlide(target, prevIndex, direction || (target > prevIndex ? 1 : -1));
      }
      if (userTriggered) this.restartAutoplay();
    }

    next(userTriggered) {
      this.goTo(this.current + 1, userTriggered, 1);
    }

    prev(userTriggered) {
      this.goTo(this.current - 1, userTriggered, -1);
    }

    preloadSlides() {
      // Lazy images sit off-screen until their turn; load them early so they never pop in mid-transition.
      const run = () => {
        this.slides.forEach((slide) => {
          slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
          });
        });
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    }

    restartDot() {
      const dot = this.dots[this.current];
      if (!dot) return;
      dot.classList.remove('is-active');
      void dot.offsetWidth;
      dot.classList.add('is-active');
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      this.restartDot();
      this.timer = window.setInterval(() => this.next(false), this.interval);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.startAutoplay();
    }

    resumeIfAllowed() {
      if (!this.userPaused) this.startAutoplay();
    }

    togglePlay() {
      this.userPaused = !this.userPaused;
      this.classList.toggle('is-paused', this.userPaused);
      if (this.playToggle) {
        this.playToggle.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        const label = this.userPaused
          ? this.playToggle.dataset.labelPlay
          : this.playToggle.dataset.labelPause;
        if (label) this.playToggle.setAttribute('aria-label', label);
      }
      if (this.userPaused) this.stopAutoplay();
      else this.startAutoplay();
    }

    handleKey(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.isRtl() ? this.prev(true) : this.next(true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.isRtl() ? this.next(true) : this.prev(true);
      }
    }

    handleTouchEnd(event) {
      if (this.touchStartX === null) return;
      const dx = event.changedTouches[0].clientX - this.touchStartX;
      const dy = event.changedTouches[0].clientY - this.touchStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) this.isRtl() ? this.prev(true) : this.next(true);
        else this.isRtl() ? this.next(true) : this.prev(true);
      }
      this.touchStartX = null;
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const slide = event.target.closest('[data-slide-index]');
      if (!slide) return;
      const index = Number(slide.dataset.slideIndex);
      if (Number.isNaN(index)) return;
      this.goTo(index, false);
      this.stopAutoplay();
      this.classList.add('is-paused');
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      if (this.userPaused) return;
      this.classList.remove('is-paused');
      this.startAutoplay();
    }

    handleSectionLoad(event) {
      if (!event.target || !event.target.contains(this)) return;
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.copies = Array.from(this.querySelectorAll('[data-hero-copy]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.indexNow = this.querySelector('[data-hero-index]');
      this.indexTotal = this.querySelector('[data-hero-total]');
      this.count = this.slides.length;
      this.interval = Number(this.dataset.interval || 6000);
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.applySlide(Math.min(this.current, Math.max(this.count - 1, 0)));
      this.startAutoplay();
    }
  }

  customElements.define('floral-hero-carousel', FloralHeroCarousel);
})();
